import {
    BarChartOutlined,
    HomeOutlined,
    SettingOutlined,
    TeamOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import { Component } from 'react';
import { Link } from 'react-router-dom';
import Routes from '../config/routes';
import { isInsightsEnabled } from '../config/settings';
import { ExtendedRoleEnum, ModuleName } from '../core/models/enum';
import { Profile } from 'oidc-client';
import authService from './api-authorization/AuthorizeService';
import { isValidationOnlyUser, SelectedUserRole } from '../helpers/CheckPermissionHelper';

interface Props {
    currentModule: string;
    userRole: SelectedUserRole;
    userProfile?: Profile;
}

interface MenuItem {
    key: ModuleName;
    icon: any;
    link: string;
    text: string;
    allowedRoles?: ExtendedRoleEnum[];
    insightsSurface?: boolean;
    children?: MenuItem[];
}

const links: MenuItem[] = [
    {
        key: ModuleName.Dashboard,
        icon: <HomeOutlined />,
        link: Routes.ROUTE_DASHBOARD,
        text: 'Home',
    },
    {
        key: ModuleName.Federations,
        icon: <UnorderedListOutlined />,
        link: Routes.ROUTE_FEDERATIONS,
        text: 'Federations',
        allowedRoles: [
            ExtendedRoleEnum.Admin,
            ExtendedRoleEnum.Federation,
            ExtendedRoleEnum.Official,
        ],
    },
    {
        key: ModuleName.Clubs,
        icon: <UnorderedListOutlined />,
        link: Routes.ROUTE_CLUBS,
        text: 'Clubs',
        allowedRoles: [ExtendedRoleEnum.Admin, ExtendedRoleEnum.Federation, ExtendedRoleEnum.Coach],
    },
    {
        key: ModuleName.Teams,
        icon: <UnorderedListOutlined />,
        link: Routes.ROUTE_TEAMS,
        text: 'Teams',
        allowedRoles: [
            ExtendedRoleEnum.Admin,
            ExtendedRoleEnum.Federation,
            ExtendedRoleEnum.Club,
            ExtendedRoleEnum.Coach,
        ],
    },
    {
        key: ModuleName.Competitions,
        icon: <UnorderedListOutlined />,
        link: Routes.ROUTE_COMPETITIONS,
        text: 'Competitions',
        allowedRoles: [
            ExtendedRoleEnum.Admin,
            ExtendedRoleEnum.Federation,
            ExtendedRoleEnum.Official,
            ExtendedRoleEnum.Club,
        ],
    },
    {
        key: ModuleName.Matches,
        icon: <UnorderedListOutlined />,
        link: Routes.ROUTE_MATCHES,
        text: 'Matches',
        allowedRoles: [
            ExtendedRoleEnum.Admin,
            ExtendedRoleEnum.Federation,
            ExtendedRoleEnum.Official,
            ExtendedRoleEnum.Club,
        ],
    },
    {
        key: ModuleName.Insights,
        icon: <BarChartOutlined />,
        link: Routes.ROUTE_INSIGHTS,
        text: 'Insights',
        insightsSurface: true,
        allowedRoles: [ExtendedRoleEnum.Admin, ExtendedRoleEnum.Federation, ExtendedRoleEnum.Club],
    },
    {
        key: ModuleName.Coaches,
        icon: <TeamOutlined />,
        link: Routes.ROUTE_COACHES,
        text: 'Coaches',
        allowedRoles: [ExtendedRoleEnum.Admin, ExtendedRoleEnum.Federation, ExtendedRoleEnum.Club],
    },
    {
        key: ModuleName.Players,
        icon: <TeamOutlined />,
        link: Routes.ROUTE_PLAYERS,
        text: 'Players',
        allowedRoles: [ExtendedRoleEnum.Admin, ExtendedRoleEnum.Federation, ExtendedRoleEnum.Club],
    },
    {
        key: ModuleName.Officials,
        icon: <TeamOutlined />,
        link: Routes.ROUTE_OFFICIALS,
        text: 'Officials',
        allowedRoles: [
            ExtendedRoleEnum.Admin,
            ExtendedRoleEnum.Federation,
            ExtendedRoleEnum.Official,
        ],
    },
    {
        key: ModuleName.Administration,
        icon: <SettingOutlined />,
        link: '',
        text: 'Administration',
        allowedRoles: [ExtendedRoleEnum.Admin],
        children: [
            {
                key: ModuleName.Users,
                icon: <TeamOutlined />,
                link: Routes.ROUTE_USERS,
                text: 'Users',
                allowedRoles: [ExtendedRoleEnum.Admin],
            },
            {
                key: ModuleName.Cities,
                icon: <HomeOutlined />,
                link: Routes.ROUTE_CITIES,
                text: 'Cities',
                allowedRoles: [ExtendedRoleEnum.Admin],
            },
            {
                key: ModuleName.ApiTokens,
                icon: <UnorderedListOutlined />,
                link: Routes.ROUTE_API_TOKENS,
                text: 'API Tokens',
                allowedRoles: [ExtendedRoleEnum.Admin],
            },
            {
                key: ModuleName.ExternalMaps,
                icon: <UnorderedListOutlined />,
                link: Routes.ROUTE_EXTERNAL_MAPS,
                text: 'External sources',
                allowedRoles: [ExtendedRoleEnum.Admin],
            },
        ],
    },
];

export default class SidebarMenu extends Component<Props> {
    getUserAllowedLinks = (menuItems: MenuItem[]): MenuItem[] => {
        const { userRole, userProfile } = this.props;

        return menuItems.filter((item: MenuItem): boolean => {
            if (item.key === ModuleName.Officials && isValidationOnlyUser(userProfile, authService.validationGamesEnabled)) return false;
            if (item.insightsSurface && !isInsightsEnabled()) return false;

            if (!item.allowedRoles) return true;

            return item.allowedRoles.some((role: ExtendedRoleEnum): boolean => {
                switch (role) {
                    case ExtendedRoleEnum.Admin:
                        return userRole.admin;
                    case ExtendedRoleEnum.Federation:
                        return userRole.federation;
                    case ExtendedRoleEnum.Club:
                        return userRole.club;
                    case ExtendedRoleEnum.Coach:
                        return userRole.coach;
                    case ExtendedRoleEnum.Official:
                        return userRole.official;
                    default:
                        return false;
                }
            });
        });
    };

    getMenuItems = (menuItems: MenuItem[]): MenuProps['items'] => {
        return this.getUserAllowedLinks(menuItems).map((item: MenuItem) => ({
            key: item.key,
            icon: item.icon,
            label: item.link ? <Link to={item.link}>{item.text}</Link> : item.text,
            children: item.children ? this.getMenuItems(item.children) : undefined,
        }));
    };

    render() {
        const { currentModule } = this.props;

        return (
            <Menu
                className="sidebar-menu"
                theme="dark"
                mode="inline"
                selectedKeys={[currentModule]}
                items={this.getMenuItems(links)}
            />
        );
    }
}
